import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/api';
import { FileText, ArrowRight, Search, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  processing: 'bg-blue-50 dark:bg-blue-950/50 text-blue-400 border-blue-900',
  review: 'bg-amber-50 dark:bg-amber-950/30 text-primary border-primary/30',
  completed: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  synced: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  published: 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400 border-emerald-900',
  created: 'bg-muted/50 text-muted-foreground border-border',
  uploaded: 'bg-muted/50 text-muted-foreground border-border',
  failed: 'bg-red-50 dark:bg-red-950/50 text-red-400 border-red-900',
};

export default function JobsListPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);

  const fetchJobs = async () => {
    try {
      const res = await api.get('/drawing-jobs');
      setJobs(res.data);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  const handleDelete = async (e, jobId, projectName) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${projectName || 'Untitled'}"? This cannot be undone.`)) return;
    setDeleting(jobId);
    try {
      await api.delete(`/drawing-jobs/${jobId}`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
      toast.success('Job deleted');
    } catch (err) {
      toast.error('Failed to delete job');
    } finally {
      setDeleting(null);
    }
  };

  const filtered = jobs.filter(
    (j) =>
      j.project_name?.toLowerCase().includes(search.toLowerCase()) ||
      j.facility_key?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="jobs-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight uppercase text-foreground">Drawing Jobs</h1>
          <p className="text-sm text-muted-foreground mt-1">{jobs.length} total extraction jobs</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="job-search-input"
            className="pl-9 pr-4 py-2 bg-card border border-border focus:border-primary rounded-sm font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none w-64 transition-colors"
            placeholder="Search projects..."
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm">
        {/* Table header */}
        <div className="grid grid-cols-[2fr_1fr_80px_80px_80px_100px_70px] gap-4 px-5 py-3 border-b border-border text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
          <span>Project</span>
          <span>Facility</span>
          <span>Sheets</span>
          <span>Spaces</span>
          <span>Assets</span>
          <span>Status</span>
          <span></span>
        </div>

        {/* Table rows */}
        <div className="divide-y divide-slate-800/30">
          {filtered.map((job) => (
            <Link
              key={job.id}
              to={`/jobs/${job.id}`}
              data-testid={`job-item-${job.id}`}
              className="grid grid-cols-[2fr_1fr_80px_80px_80px_100px_70px] gap-4 px-5 py-3 items-center hover:bg-secondary/20 transition-colors group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-mono text-sm text-foreground truncate group-hover:text-primary transition-colors">
                    {job.project_name || 'Untitled'}
                  </p>
                  <p className="text-[10px] font-mono text-muted-foreground">{job.revision}</p>
                </div>
              </div>
              <span className="font-mono text-xs text-muted-foreground truncate">{job.facility_key}</span>
              <span className="font-mono text-xs text-foreground/80">{job.sheet_count}</span>
              <span className="font-mono text-xs text-foreground/80">{job.space_count}</span>
              <span className="font-mono text-xs text-foreground/80">{job.asset_count}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide border w-fit ${statusColors[job.status] || statusColors.created}`}>
                {job.status}
              </span>
              <div className="flex items-center gap-1 justify-end">
                <button
                  onClick={(e) => handleDelete(e, job.id, job.project_name)}
                  disabled={deleting === job.id}
                  data-testid={`delete-job-${job.id}`}
                  className="p-1.5 text-muted-foreground hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all rounded hover:bg-rose-950/30"
                  title="Delete job"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-muted-foreground text-sm">No jobs found</div>
        )}
      </div>
    </div>
  );
}
